import {
  Alert,
  Anchor,
  Button,
  Container,
  PasswordInput,
  Stack,
  TextInput,
  Title,
} from "@mantine/core";
import type { FormEvent, ReactElement } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { readRedirectTarget } from "../auth/redirectTarget";
import { HTTP_CONFLICT, HttpError } from "../features/auth/api";
import { useRegister } from "../features/auth/useRegister";

// Страница `/register`.
//
// На success — useRegister уже положил org-less identity в store
// (ADR-0038, фаза 1; авто-логин); здесь редирект на `/select-org`
// (фаза 2 — обеспечить активную организацию), а не сразу в раздел.
// `state.from` — путь, куда `/select-org` вернёт после выбора org
// (для прямого захода на `/register` даёт дефолт `/home` — развилку
// в первый доступный раздел, см. `readRedirectTarget`). На 409 — Alert «email занят», на другие
// ошибки — generic.
export function RegisterPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const register = useRegister();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    register.mutate(
      { email, password },
      {
        onSuccess: () => {
          void navigate("/select-org", {
            replace: true,
            state: { from: readRedirectTarget(location.state) },
          });
        },
      },
    );
  };

  const errorBanner = ((): ReactElement | null => {
    if (!register.isError) {
      return null;
    }
    if (
      register.error instanceof HttpError &&
      register.error.status === HTTP_CONFLICT
    ) {
      return (
        <Alert color="red" title={t("auth.errorHeading")}>
          {t("auth.emailTaken")}
        </Alert>
      );
    }
    return (
      <Alert color="red" title={t("auth.errorHeading")}>
        {t("auth.genericError", { message: register.error.message })}
      </Alert>
    );
  })();

  return (
    <Container py="xl" size="xs">
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <Title order={1}>{t("auth.registerTitle")}</Title>

          <TextInput
            label={t("auth.emailLabel")}
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.currentTarget.value);
            }}
            required
            autoComplete="email"
          />
          <PasswordInput
            label={t("auth.passwordLabel")}
            description={t("auth.passwordHint")}
            value={password}
            onChange={(event) => {
              setPassword(event.currentTarget.value);
            }}
            required
            // OWASP min length = 8 (см. RegisterRequest на бэке).
            minLength={8}
            autoComplete="new-password"
          />

          {errorBanner}

          <Button type="submit" loading={register.isPending}>
            {t("auth.registerButton")}
          </Button>

          <Anchor component={Link} to="/login" size="sm">
            {t("auth.toLogin")}
          </Anchor>
        </Stack>
      </form>
    </Container>
  );
}
